import { IntegrationFunnel, nonStandardPicks } from '@denqfabrik/landing-ui';
import { useMemo } from 'react';
import {
  buildFunnelConfig,
  buildFunnelContact,
  buildFunnelLabels,
  buildResolveOutcome,
  FUNNEL_LEAD_TYPE,
  ZOOM_URL,
} from '../data/integrations';
import { useTranslation } from '../i18n';
import { pushLead } from '../utils/analytics';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * The configurator as an overlay, opened from the navbar CTA (René, 2026-08-12:
 * "not an own page. more a module opening when pressing on the button").
 *
 * This is now the ONLY instance of the funnel (René, 2026-08-13). The inline copy on
 * /integrationen was removed the same day; that page runs hero → approach cards →
 * platform grid → CTA and no longer renders the configurator at all.
 *
 * The SEO trade-off was made knowingly and is not an oversight to "fix": the funnel is
 * behind a click, so its copy is no longer crawlable and no longer carries landing-page
 * relevance for the ads. Deliberately NOT an on-load interstitial either — Google
 * demotes those on mobile. If ad relevance needs a crawlable block again, it has to be
 * written as page copy, not by re-mounting the funnel inline.
 */
export default function FunnelModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useTranslation();
  const f = t.funnel;

  // Built once per language, not once per render: IntegrationFunnel memoises on the
  // `config` identity and keeps `resolveOutcome` in an effect dep chain, so fresh
  // objects would recompute the funnel on every render and re-arm the completion
  // effect. `f` is a module-level const per language, so this changes only on switch.
  const config = useMemo(() => buildFunnelConfig(f), [f]);
  const labels = useMemo(() => buildFunnelLabels(f), [f]);
  const resolveOutcome = useMemo(() => buildResolveOutcome(f), [f]);

  /**
   * The LEAD event, and the only one. It hangs on the awaited `/api/contact` POST:
   * `buildFunnelContact`'s `onSubmit` throws on a non-2xx (`integrations.ts:353`), so
   * a push here is reached only when the request actually succeeded — a 500, an
   * offline visitor or a spam-guard rejection reports nothing.
   *
   * Why it is NOT on `onComplete`: that fires when the 4th multiple-choice answer
   * lands (`IntegrationFunnel.tsx:304`), which is BEFORE the contact step renders
   * at all. Reporting there counted everyone who played with the configurator and
   * nobody who actually submitted — €416 of Google Ads spend over 30 days bought zero
   * verifiable conversions (elon, 2026-09-15).
   *
   * Goes through `pushLead`, the app's single lead-reporting helper, which the
   * working `/kontakt` → `/danke` path uses too — so both lead sources land in ONE
   * GTM trigger and one GA4 key event instead of splitting the segment.
   */
  const contact = useMemo(() => {
    const base = buildFunnelContact(f);
    return {
      ...base,
      onSubmit: async (...args: Parameters<typeof base.onSubmit>) => {
        await base.onSubmit(...args);
        pushLead({
          // Not a free label: FUNNEL_LEAD_TYPE is the same constant this funnel posts
          // as `selectedOption` to /api/contact, which is what /danke reads into
          // `lead_type`. One constant, so the two cannot drift apart.
          leadType: FUNNEL_LEAD_TYPE,
          formLocation: 'funnel_modal',
          language: lang,
        });
      },
    };
  }, [f, lang]);

  return (
    <IntegrationFunnel
      presentation="modal"
      open={open}
      onClose={onClose}
      config={config}
      labels={labels}
      resolveOutcome={resolveOutcome}
      contact={contact}
      // Secondary: mail first, a call only if the visitor wants one (PRICING-STRATEGY
      // §0.0, 2026-09-21). Without a variant this link rendered filled-primary ABOVE
      // the contact form, so the call read as the main next step.
      actions={[{ id: 'zoom', label: f.bookCall, href: ZOOM_URL, variant: 'secondary' }]}
      // Ids, never labels: the labels are localized since 2026-08-13, and pushing them
      // would split every GTM segment in two — "Bestandsabgleich" from /de against
      // "Inventory sync" from /en for the same choice.
      // ENGAGEMENT, never a conversion. Renamed from `funnel_complete` on 2026-09-15:
      // GTM (GTM-TL4LGR65) mapped that name onto GA4 `generate_lead`, so finishing the
      // four questions counted as a lead. The rename disarms that mapping by itself —
      // the old trigger can no longer fire — while keeping the configurator funnel
      // visible as engagement. Do not map this event to a conversion.
      onComplete={(answers) => {
        window.dataLayer = window.dataLayer ?? [];
        window.dataLayer.push({
          event: 'funnel_engagement',
          funnel_placement: 'navbar_modal',
          funnel_source: answers.sourceSlug ?? 'custom',
          funnel_targets: answers.targetSlugs.join(','),
          funnel_flows: answers.flowIds.join(','),
          funnel_non_standard: nonStandardPicks(config.catalogue, answers)
            .map((flow) => flow.id)
            .join(','),
          funnel_cadence: answers.scaleId ?? '',
        });
      }}
    />
  );
}
